import { Link } from 'react-router-dom';
import { useEffect, useRef} from 'react';
import './../retail.scss';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import retailSquare from './../../assets/images/retail-shapes-square.svg';
import retailCircle from './../../assets/images/retail-shapes-circle.svg';
import retailTriangle from './../../assets/images/retail-shapes-triangle.svg';
import retailUsageBarChart from './../../assets/images/retail-usage-bar-chart.svg';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function RetailUsage() {

  const squareRef = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.height = fill + '%';
          }

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }

        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(squareRef.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="retail-usage">

        <p id="s1" className="intro" data-aos="fade-up">Explore key stats and insights to see how today’s retail businesses leverage direct mail.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Marketing Effectiveness</h3>

                <p className="pull-quote"><span data-aos className='highlighter'>73%</span> of retail companies report that direct mail is <span data-aos className='highlighter'>very or extremely effective.</span></p>

                <p>Retail companies consider social media the most effective channel.</p>

                <p>Retail companies find mail to be more effective than newspaper/magazine, radio and billboard/outdoor, but less effective than TV, social, email, internet and paid search.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Direct mail is most effective when used as a part of omnichannel campaigns. Making use of multiple mediums is important, but without proper integration, marketing may end up disjointed, disconnected and ineffectual. <Link to="/retail/integration">Explore the Integration section</Link> for insights and tips on creating seamless, well-connected campaigns.</p>
              </div>
            </div>

            <div className='inner-cols'>
              <div className='inner-col-item'>

                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Direct Mail Techniques</h3>
                    <div className='progress-bar' data-aos="horz-bar-chart" data-aos-id="bar-anim">
                      <div className='progress' data-fill="69"><span class="percentage" data-to="69" aria-hidden="true">0</span><span class="screen-reader-text">69</span>%</div>
                    </div>
                    <p>Personalized offers</p>

                    <p><strong>61%</strong> — Personalized greetings or customer names</p>

                    <p>Among retail companies, personalization is the most common direct mail technique.</p>

                    
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p>Personalized direct mail offers can be an effective way to encourage customers to act. Use data to create customized promos, coupons and incentives. <a href="/../evolution-of-a-medium/retargeted-direct-mail">Retargeted direct mail</a>, for example, can help you follow up with customers who left items in their online shopping carts; including a limited-time discount can help seal the deal.</p>
                  </div>
                </div>
              </div>

              <div className='inner-col-item'>
                <div className='personalized-box align-to-text'>
                  <h3>Retail businesses typically use mail to drive awareness.</h3>
                  <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                    <div className='triangle triangle-1'>
                      <img ref={triangle1Ref} src={retailTriangle} alt="" />
                    </div>
                    <div className='square'>
                      <img ref={squareRef} src={retailSquare} alt="" />
                    </div>
                    <div className='triangle triangle-2'>
                      <img ref={triangle2Ref} src={retailTriangle} alt="" />
                    </div>
                    <div className='circle'>
                      <img src={retailCircle} alt="" />
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
          <div className='col-right'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail and the Customer Journey</h3>

                <div className='progress-bar-area vertical-bar-chart' data-aos data-aos-id="bar-anim" data-aos-offset="300">
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="35"><span class="percentage" data-to="35" aria-hidden="true">0</span><span class="screen-reader-text">35</span>%</div>
                    </div>
                    <span className='label'>Drive awareness</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress' data-fill="22"><span class="percentage" data-to="22" aria-hidden="true">0</span><span class="screen-reader-text">22</span>%</div>
                    </div>
                    <span className='label'>Drive growth</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="18"><span class="percentage" data-to="18" aria-hidden="true">0</span><span class="screen-reader-text">18</span>%</div>
                    </div>
                    <span className='label'>Drive leads</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="25"><span class="percentage" data-to="25" aria-hidden="true">0</span><span class="screen-reader-text">25</span>%</div>
                    </div>
                    <span className='label'>Drive loyalty</span>
                  </div>
                </div>

                <p>In relation to the customer journey, most retail companies use direct mail toward the beginning of the customer journey, to drive awareness.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Bold, eye-catching design and clear, simple messaging—on easy-to-read, immediately digestible formats like postcards—can raise awareness of your brand and offerings. This also provides a uniquely tangible experience and can be a welcome change of pace from the countless digital ads customers are exposed to every day.</p>
              </div>
            </div>
            
            
          </div>
        </div>



      </div>
    </>
  );
}
